import React from 'react';
import { Info, Sprout, FileText, Award, Briefcase, TrendingUp, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о сельскохозяйственных субсидиях и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Государственная поддержка сельского хозяйства: виды, условия, требования
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Sprout className="w-5 h-5 text-emerald-500" />
          Что такое сельскохозяйственные субсидии?
        </h3>
        <p>
          <strong>Сельскохозяйственные субсидии</strong> — это форма государственной поддержки сельхозпроизводителей, 
          направленная на обеспечение продовольственной безопасности страны, развитие сельских территорий, 
          повышение конкурентоспособности отечественной продукции и помощь аграриям в преодолении финансовых трудностей.
        </p>
        <p>
          Субсидии выделяются из федерального и региональных бюджетов в рамках государственных программ 
          развития сельского хозяйства и могут иметь форму прямых выплат, компенсаций затрат, льготного кредитования, 
          грантов на развитие и других видов поддержки.
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-500" />
          Основные виды и направления субсидирования
        </h3>
        <h4>1. Капитальное субсидирование</h4>
        <ul>
          <li><strong>Гранты на создание и развитие КФХ</strong> — для начинающих фермеров на приобретение земли, техники, оборудования (до 5 млн рублей)</li>
          <li><strong>Субсидии на модернизацию</strong> — компенсация до 70% затрат на обновление оборудования, строительство и реконструкцию объектов</li>
          <li><strong>Поддержка семейных ферм</strong> — гранты на развитие животноводческих и растениеводческих ферм (до 30 млн рублей)</li>
          <li><strong>Агротуризм</strong> — гранты на создание и развитие объектов сельского туризма (до 10 млн рублей)</li>
        </ul>

        <h4>2. Операционные субсидии</h4>
        <ul>
          <li><strong>Несвязанная поддержка</strong> — субсидия на гектар посевной площади, зависит от региона и направления производства</li>
          <li><strong>Субсидирование затрат на семена</strong> — компенсация части затрат на приобретение элитных семян</li>
          <li><strong>Поддержка животноводства</strong> — субсидии на 1 кг произведенного и реализованного молока, на содержание племенного скота</li>
          <li><strong>Компенсация части затрат на удобрения и СЗР</strong> — субсидии на приобретение минеральных удобрений и средств защиты растений</li>
        </ul>

        <h4>3. Льготное кредитование и субсидирование процентных ставок</h4>
        <ul>
          <li><strong>Льготный краткосрочный кредит</strong> — до 1 года под 1-5% годовых для проведения сезонных работ</li>
          <li><strong>Льготный инвестиционный кредит</strong> — от 2 до 15 лет под 1-5% годовых на развитие производства</li>
          <li><strong>Субсидирование процентной ставки</strong> — компенсация части процентных выплат по коммерческим кредитам</li>
        </ul>

        <h4>4. Поддержка экспортной деятельности</h4>
        <ul>
          <li><strong>Компенсация транспортных расходов</strong> — при экспорте сельскохозяйственной продукции</li>
          <li><strong>Субсидирование сертификации</strong> — компенсация затрат на сертификацию продукции для зарубежных рынков</li>
          <li><strong>Льготное страхование экспортных контрактов</strong> — снижение рисков при внешнеторговой деятельности</li>
        </ul>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium">Региональные особенности субсидирования</h4>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            Помимо федеральных программ, каждый регион разрабатывает собственные программы поддержки 
            сельхозпроизводителей с учетом местной специфики. Процент компенсации затрат, размер грантов 
            и приоритетные направления могут значительно отличаться в зависимости от региона и его 
            агроклиматических особенностей.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Award className="w-5 h-5 text-emerald-500" />
          Кто может претендовать на субсидии?
        </h3>
        <p>
          На получение государственной поддержки могут претендовать сельхозпроизводители различных 
          организационно-правовых форм при соответствии следующим базовым требованиям:
        </p>
        <ul>
          <li>Регистрация в качестве сельхозтоваропроизводителя (доля дохода от сельхозпродукции не менее 70%)</li>
          <li>Отсутствие задолженности по налогам и сборам</li>
          <li>Отсутствие процедуры банкротства</li>
          <li>Наличие производственных мощностей и земельных участков, соответствующих заявленной деятельности</li>
          <li>Для грантовой поддержки — наличие бизнес-плана и софинансирования (обычно 20-40% от стоимости проекта)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-emerald-500" />
          Порядок получения субсидий
        </h3>
        <ol>
          <li><strong>Анализ доступных программ</strong> — изучение условий федеральных и региональных программ поддержки</li>
          <li><strong>Подготовка документации</strong> — сбор всех необходимых документов и составление бизнес-плана</li>
          <li><strong>Подача заявки</strong> — в региональный департамент сельского хозяйства или другой уполномоченный орган</li>
          <li><strong>Рассмотрение заявки</strong> — проверка соответствия требованиям и конкурсный отбор</li>
          <li><strong>Заключение соглашения</strong> — при одобрении заявки подписывается соглашение о предоставлении субсидии</li>
          <li><strong>Получение средств</strong> — перечисление денег на счет или компенсация уже понесенных затрат</li>
          <li><strong>Отчетность</strong> — предоставление отчетов о целевом использовании полученных средств</li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить особое внимание
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно изучите требования и условия каждой программы субсидирования</li>
            <li>Соблюдайте сроки подачи документов и отчетности</li>
            <li>Обеспечьте прозрачность финансовых операций и строгое целевое использование средств</li>
            <li>Храните все подтверждающие документы о расходах и полученных результатах</li>
            <li>Обратите внимание на обязательства по созданию рабочих мест, достижению производственных показателей</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          Как повысить шансы на получение субсидий?
        </h3>
        <ul>
          <li><strong>Диверсификация производства</strong> — развитие нескольких направлений для снижения рисков</li>
          <li><strong>Внедрение инноваций</strong> — использование современных технологий повышает конкурентоспособность</li>
          <li><strong>Экологизация производства</strong> — соответствие экологическим стандартам дает преимущества</li>
          <li><strong>Создание рабочих мест</strong> — проекты с большим социальным эффектом имеют приоритет</li>
          <li><strong>Качественный бизнес-план</strong> — детальное экономическое обоснование проекта с реалистичными показателями</li>
          <li><strong>Правильное оформление документов</strong> — точность и полнота документации существенно повышает шансы</li>
        </ul>

        <h3>Заключение</h3>
        <p>
          Государственная поддержка сельского хозяйства — важный инструмент для развития аграрного сектора.
          Грамотное использование доступных программ субсидирования может существенно снизить финансовую нагрузку, 
          ускорить модернизацию производства и повысить рентабельность сельхозпредприятия.
        </p>
        
        <p className="font-semibold mt-6">
          Наш калькулятор сельскохозяйственных субсидий поможет вам оценить объем доступной государственной поддержки 
          и спланировать развитие вашего сельскохозяйственного бизнеса!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;